import React, { useState, useEffect, useRef } from 'react';
import { Search, Download, ChevronDown } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexHistAdmision({ readOnly = false, hideHeader = false }) {
    const [estudiantes, setEstudiantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterYear, setFilterYear] = useState('todos');

    useEffect(() => {
        loadHistorial();
    }, []);

    const loadHistorial = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/kardex/historial`);
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading historial:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (estudiante) => {
        try {
            // Obtener las notas del estudiante
            const notasResponse = await fetch(`${API_URL}/kardex/historial/${estudiante.id_estudiante}/notas`);
            const notasData = await notasResponse.json();
            const notas = notasData.notas || [];

            // Función para convertir nota numérica a literal
            const notaToLiteral = (nota) => {
                if (!nota || nota === '-') return '-';
                const n = parseFloat(nota);
                if (n >= 90) return 'NOVENTA Y ' + (n === 100 ? 'CIEN' : ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'][n % 10] || '');
                if (n >= 80) return 'OCHENTA Y ' + ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'][n % 10];
                if (n >= 70) return 'SETENTA Y ' + ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'][n % 10];
                if (n >= 60) return 'SESENTA Y ' + ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'][n % 10];
                if (n >= 51) return 'CINCUENTA Y ' + ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'][n % 10];
                return 'REPROBADO';
            };

            const getEstado = (nota) => {
                if (!nota || nota === '-') return '-';
                return parseFloat(nota) >= 51 ? 'APROBADO' : 'REPROBADO';
            };

            // Crear datos para Excel
            const wsData = [];

            // Encabezado principal
            wsData.push(['', '', '', 'HISTORIAL ACADÉMICO', '', '', '', '', '', '']);
            wsData.push(['', '', '', 'PLAN DE ESTUDIOS: R.M. N°1040/2022 - R.M. N°0072/2024', '', '', '', '', '', '']);
            wsData.push([]);

            // Datos del estudiante - lado izquierdo y derecho
            const nombreCompleto = `${estudiante.nombres} ${estudiante.apellido_paterno || ''} ${estudiante.apellido_materno || ''}`.trim().toUpperCase();
            wsData.push(['ESTUDIANTE: ' + nombreCompleto, '', '', '', 'CÓDIGO ESTUDIANTIL: ' + (estudiante.codigo_estudiante || '-')]);
            wsData.push(['', '', '', '', 'CÉDULA DE IDENTIDAD: ' + (estudiante.cedula_identidad || '-') + ' ' + (estudiante.extension || '')]);
            wsData.push(['CARRERA: ' + (estudiante.carrera_nombre || '-').toUpperCase(), '', '', '', 'AÑO DE MODALIDAD DE ADMISIÓN: ' + (estudiante.anio_modalidad || estudiante.anio_inicio || '-')]);
            wsData.push(['MENCIÓN: ' + (estudiante.mencion_nombre || '-').toUpperCase(), '', '', '', 'AÑO DE INICIO CARRERA: ' + (estudiante.anio_inicio || '-')]);
            wsData.push(['NIVEL DE FORMACIÓN: ' + (estudiante.nivel_formacion || 'LICENCIATURA').toUpperCase(), '', '', '', 'AÑO DE CONCLUSIÓN DE CARRERA: ' + (estudiante.anio_conclusion || '-')]);
            wsData.push(['RÉGIMEN: ' + (estudiante.regimen || 'ANUAL').toUpperCase(), '', '', '', 'FOJAS: 1 DE 1']);
            wsData.push([]);

            // Título de la tabla
            wsData.push(['PROCESO DE CONVALIDACIÓN DE ASIGNATURAS DEL PLAN DE ESTUDIOS 2019 AL PLAN 2022 AL 2024']);
            wsData.push([]);

            // Encabezados de la tabla
            wsData.push([
                'N°',
                'NIVEL DE FORMACIÓN',
                'CÓDIGO',
                'ASIGNATURA',
                'CARGA HORARIA ANUAL',
                'NOTA FINAL',
                'PRUEBA RECUP.',
                'NOTA FINAL (LITERAL)',
                'ESTADO',
                'OBSERVACIONES'
            ]);

            // Datos de notas
            if (notas.length === 0) {
                wsData.push(['', 'No hay notas registradas', '', '', '', '', '', '', '', '']);
            } else {
                notas.forEach((nota, index) => {
                    // Si hay segunda_turno (recuperación) con valor > 0, se usa
                    // SOLO para calcular Literal y Estado. Nota Final se muestra tal cual.
                    const notaParaEstado = (nota.nota_recuperacion && parseFloat(nota.nota_recuperacion) > 0)
                        ? nota.nota_recuperacion
                        : nota.nota_final;

                    wsData.push([
                        index + 1,
                        nota.nivel_formacion || 'PRIMER AÑO',
                        nota.codigo_asignatura || '-',
                        nota.nombre_asignatura || '-',
                        nota.carga_horaria ? nota.carga_horaria + ' horas' : '160 horas',
                        nota.nota_final || '-',
                        nota.nota_recuperacion || '',
                        notaToLiteral(notaParaEstado),
                        getEstado(notaParaEstado),
                        nota.observaciones || ''
                    ]);
                });
            }

            wsData.push([]);
            wsData.push(['Generado el: ' + new Date().toLocaleDateString('es-BO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            })]);

            // Crear workbook y worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // Ajustar anchos de columna
            ws['!cols'] = [
                { wch: 5 },   // N°
                { wch: 18 },  // Nivel
                { wch: 12 },  // Código
                { wch: 35 },  // Asignatura
                { wch: 15 },  // Carga horaria
                { wch: 10 },  // Nota final
                { wch: 12 },  // Prueba recup
                { wch: 20 },  // Nota literal
                { wch: 12 },  // Estado
                { wch: 15 }   // Observaciones
            ];

            XLSX.utils.book_append_sheet(wb, ws, 'Historial Académico');

            // Descargar usando Blob para asegurar extensión .xlsx
            const fileName = `Historial_Academico_${estudiante.cedula_identidad || estudiante.id_estudiante}_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Generar el archivo como array buffer
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

            // Crear blob con el tipo MIME correcto
            const blob = new Blob([wbout], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Crear link y descargar
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error al descargar:', error);
            alert('Error al descargar el historial académico');
        }
    };

    /* =========================
       AÑOS DINÁMICOS (2020 → año actual + 1)
    ========================== */
    const currentYear = new Date().getFullYear();
    const years = [
        'todos',
        ...Array.from(
            { length: currentYear - 2020 + 2 },
            (_, i) => 2020 + i
        )
    ];

    /* =========================
       FILTROS
    ========================== */
    const filteredEstudiantes = estudiantes.filter(est => {
        const matchesSearch =
            searchTerm === '' ||
            `${est.nombres} ${est.apellido_paterno} ${est.cedula_identidad} ${est.codigo_estudiante}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase());

        const matchesYear =
            filterYear === 'todos' ||
            String(est.anio_inicio || '') === String(filterYear);

        return matchesSearch && matchesYear;
    });

    return (
        <div className="p-6">
            {/* Header */}
            {/* {!readOnly && (
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Historial Académico</h1>
                    <p className="text-sm text-gray-600 mt-1">Kardex &gt; Historial Académico</p>
                </div>
            )} */}
            {/* Header condicional */}
            {!hideHeader && !readOnly && (
                <div className="mb-6">
                    <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Historial Académico</h1>
                </div>
            )}

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre o cédula..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>
                                    {year === 'todos' ? 'Todos los años' : year}
                                </option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>
                </div>
            </div>

            {/* Filters */}
            {/* <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                   
                    <div className="flex-1 relative max-w-md">
                        <input
                            type="text"
                            placeholder="Buscar por nombre o cédula..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={20}
                        />
                    </div>

                    <div className="relative">
                        <select
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                            className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>
                                    {year === 'todos' ? 'Todos los años' : year}
                                </option>
                            ))}
                        </select>
                        <ChevronDown
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            size={18}
                        />
                    </div>
                </div>
            </div> */}

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Cargando...</div>
                ) : (
                    <div
                        className="overflow-x-auto overflow-y-auto max-h-[65vh] table-scroll"
                    >
                        <table className="w-full text-sm">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-3 text-center font-semibold border">No</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Código</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Nombres</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Apellidos</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Cédula</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Carrera</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Mención</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Tipo</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Email</th>
                                    <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {filteredEstudiantes.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                                            No hay estudiantes registrados
                                        </td>
                                    </tr>
                                ) : (
                                    filteredEstudiantes.map((est, index) => (
                                        <tr key={est.id_estudiante} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 border text-center">{index + 1}</td>
                                            <td className="px-3 py-2 border font-medium">{est.codigo_estudiante}</td>
                                            <td className="px-3 py-2 border">{est.nombres}</td>
                                            <td className="px-3 py-2 border">
                                                {`${est.apellido_paterno || ''} ${est.apellido_materno || ''}`.trim()}
                                            </td>
                                            <td className="px-3 py-2 border">{est.cedula_identidad}</td>
                                            <td className="px-3 py-2 border">{est.carrera_nombre || '-'}</td>
                                            <td className="px-3 py-2 border">{est.mencion_nombre || '-'}</td>
                                            <td className="px-3 py-2 border">
                                                <span
                                                    className={`px-2 py-1 rounded-full text-xs font-medium ${est.tipo === 'admision'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-green-100 text-green-800'
                                                        }`}
                                                >
                                                    {est.tipo === 'admision' ? 'Admisión' : 'Matriculación'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 border">{est.email || '-'}</td>
                                            <td className="px-3 py-2 border">
                                                <button
                                                    onClick={() => handleDownload(est)}
                                                    className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition shadow-sm"
                                                    title="Descargar historial académico"
                                                >
                                                    <Download size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Summary */}
            <div className="mt-4 text-sm text-gray-600">
                Total: {filteredEstudiantes.length} estudiante(s)
            </div>
        </div>
    );
}
